import { useEffect, useMemo, useRef, useState } from "react";

import { PennButton } from "@/components/ui";
import type {
  ConfigureFamily,
  ConfigureOptionDto,
  ConfigureOptionsResponse,
  ToolingGroupDto,
} from "@/api/configure-options";
import type { RegionCode, SelectorProduct } from "@/api/filter";
import type { SnapshotSelectedOptionInput } from "@/api/tasks";
import type { DocManualLineItem } from "@/api/quoteDocuments";
import { useConfigureOptions } from "@/hooks/useConfigureOptions";
import { useTask } from "@/hooks/useTasks";
import {
  useManualQuoteItems,
  useSaveManualQuoteItem,
} from "@/hooks/useManualQuoteItems";
import type { ManualQuoteItem } from "@/api/manualQuoteItems";
import {
  useAddManualLineItem,
  useDeleteManualLineItem,
  useManualLineItems,
} from "@/hooks/useManualLineItems";
import type { ManualLineItem } from "@/api/manualLineItems";
import { formatMoney, parseDecimalString } from "@/utils/cost-display";
import {
  applyAutoSelect,
  mspAutoFeedManagedPartNos,
  mspAutoFeedUpgradePartNos,
  syncMspAutoFeedSelection,
} from "@/utils/configure-auto-select";
import { type MdsFilterState } from "@/utils/mds-configure";
import {
  buildCatalogGroups,
  getCustomToolingItem,
  optionDisplayTitle,
  sortOptionRows,
  type CatalogGroup,
} from "@/utils/configure-catalog-display";
import {
  buildSnapshotSelectedOptions,
  configureCatalogItems,
  configureSelectablePartNos,
  configureUnitPrices,
} from "@/utils/configure-selection-preview";
import { dispatchCompareCostInvalidate } from "@penn/web-common";
import { toCalculationRateLimitError } from "@/utils/calculation-request-control";
import {
  CUSTOM_TOOLING_PART_NO,
  applyUserSelectionOverrides,
  itemsToSelectionMap,
  loadProductSelection,
  manualLineItemsForCurrency,
  overridesToSelectionMap,
  replaceManualLineItemsForCurrency,
  saveProductSelection,
  selectionMapToItems,
  selectionOverridesToItems,
  type ConfigureProductSelection,
} from "@/utils/configure-storage";

import { TaskSidePanel, type TaskContext } from "./TaskSidePanel";

// =============================================================================
// ConfigureSolutionModal — Phase 2 (POC parity, full inner-content rewrite).
//
// Wrapper / header / TaskSidePanel scaffold preserved; body replaced with the
// configure-options API integration. See spec
// `docs/spec/configure-solution-machine-options.md` (D1–D11 + Q1/Q5/Q6/Q7).
//
// State machine:
// 1. Modal opens with `productId` + snapshot props (region, applicationIds).
// Region is locked at open (Q7). Compare-card auto-feed (D9) stays live
// for the modal lifetime so toggling on the card re-syncs selections.
// 2. `useConfigureOptions` fetches.
// 3. On first successful load: compute current recommendations, then apply the
// persisted user selections/deselections/quantities last (CR-006).
// 4. User edits selection / qty or non-task manual lines; write the workspace
// draft back to localStorage on every change.
// 5. Profil element-type dropdown / MDS filter bar render when meta carries
// the relevant blocks.
//
// Anti-patterns avoided:
// - No `Number()` / `parseFloat()` on price strings (rule §D.12).
// - No render-time API calls; the hook is the only network surface.
// - No silent ASIA → NA fallback; the response carries the typed unavailable.
// =============================================================================

type ConfigureSolutionModalProps = {
  open: boolean;
  /**
   * `changed` is true when the user actually edited the configuration in this
   * session (options / quantities / profil element type / MDS filters / custom
   * tooling) — NOT for the auto-selection persisted on open. The parent uses it
   * to decide whether to re-run Optimize Allocation.
   */
  onClose: (changed?: boolean) => void;
  productId: string;
  product: SelectorProduct;
  region: RegionCode;
  applicationIds: string[];
  mspAutoFeedEnabled: boolean;
  /** Comparison preview `summary.requiredMachines` (POC cost calc). */
  requiredMachines: number;
  canShowPrices: boolean;
  isAuthenticated: boolean;
  /** Whether the current user may generate/download a quotation (matrix
   * `canGenerateQuote`). Also gates writing the shared reusable line-item
   * library (POC parity: quote-capable staff can save reusable items). */
  canGenerateQuote?: boolean;
  /** The RFQ-context side panel is a staff surface (customer contact details,
   * approval decisions, finalize). Hidden from users without
   * `canWorkForOthers`, who open their own task read-only. */
  canWorkForOthers?: boolean;
  /** D7 (POC parity): app + element-type counts shown in the configure header. */
  appSummary?: { applications: string[]; elementTypes: string[] };
  taskContext?: TaskContext;
  /** D2 (POC parity): frozen rows and quantities restored from the task. An
   * explicit empty array preserves a deliberate zero-option snapshot. */
  initialSelectedOptions?: SnapshotSelectedOptionInput[];
  /** Legacy fallback for snapshots/readers that expose only part numbers. */
  initialSelectedPartNos?: string[];
  /** D2: the Custom Tooling free text frozen on the task's snapshot. */
  initialCustomToolingText?: string | null;
  /** RFQ submit — passes the frozen option selection up to the page. */
  onRequestQuotation?: (payload: ConfigurePayload) => void;
  /** Ad-hoc quotation download (.pdf) — passes the selection up to the page. */
  onDownloadQuotation?: (payload: ConfigurePayload) => void;
  /** Ad-hoc Word download (.docx) — passes the selection up to the page. */
  onDownloadWord?: (payload: ConfigurePayload) => void;
};

/**
 * Shared payload for the three Configure-popup actions. `manualLineItems` are
 * the user-added ad-hoc lines (POC parity: kept local to the popup and folded
 * into the downloaded quotation / Word and the RFQ snapshot on submit).
 */
export type ConfigurePayload = {
  selectedPartNos: string[];
  selectedOptions: SnapshotSelectedOptionInput[];
  manualLineItems: DocManualLineItem[];
};

/** Local draft for the "Add manual line item" modal (POC `manualLineItemModal`). */
type ManualDraft = {
  category: string;
  partNo: string;
  description: string;
  qty: string;
  price: string;
  // POC "Advanced fields (optional)" + reusable-library plumbing.
  label: string;
  itemName: string;
  partName: string;
  machineType: string;
  toolingType: string;
  /** When set, the draft was filled from this library item (PATCH target). */
  selectedLibraryId: string | null;
  /** POC "Also save/update in reusable library" — admin-only. */
  saveToLibrary: boolean;
};

type ConfigureDraftState = {
  selection: Map<string, number>;
  /** Explicit checkbox/quantity edits; zero is a deselection tombstone. */
  userOverrides: Map<string, number>;
  /** Task snapshots remain exact rather than being recommendation overlays. */
  selectionFrozen: boolean;
};

const MANUAL_SECTIONS = [
  "Machine options",
  "Tooling",
  "Service",
  "Machine",
] as const;

const EMPTY_MANUAL_DRAFT: ManualDraft = {
  category: MANUAL_SECTIONS[0],
  partNo: "",
  description: "",
  qty: "1",
  price: "",
  label: "",
  itemName: "",
  partName: "",
  machineType: "",
  toolingType: "",
  selectedLibraryId: null,
  saveToLibrary: false,
};

/** Normalise a library item's free-text category onto the 4 POC sections. */
const normalizeManualSection = (raw: string | null | undefined): string => {
  const value = String(raw ?? "").trim();
  const match = MANUAL_SECTIONS.find(
    (s) => s.toLowerCase() === value.toLowerCase(),
  );
  return match ?? MANUAL_SECTIONS[0];
};

/** Map a persisted per-task manual line (D4) onto the popup's render/quote shape. */
const taskItemToDoc = (i: ManualLineItem): DocManualLineItem => ({
  description: i.description ?? "",
  price: i.price ?? "0",
  qty: i.qty ?? 1,
  currency: i.currency ?? "EUR",
  partNo: i.partNo,
  category: i.category,
  itemName: i.itemName,
});

export const ConfigureSolutionModal = ({
  open,
  onClose,
  productId,
  product,
  region,
  applicationIds,
  mspAutoFeedEnabled,
  requiredMachines,
  canShowPrices,
  isAuthenticated,
  canGenerateQuote = false,
  canWorkForOthers = false,
  appSummary,
  taskContext,
  initialSelectedOptions,
  initialSelectedPartNos,
  initialCustomToolingText,
  onRequestQuotation,
  onDownloadQuotation,
  onDownloadWord,
}: ConfigureSolutionModalProps) => {
  const query = useConfigureOptions({
    productId,
    region,
    applicationIds,
    mspAutoFeedEnabled,
    enabled: open,
  });
  const response = query.data;
  const family: ConfigureFamily | null = response?.meta.configureFamily ?? null;

  const [draftState, setDraftState] = useState<ConfigureDraftState>(() => ({
    selection: new Map(),
    userOverrides: new Map(),
    selectionFrozen: false,
  }));
  const [hydratedDraftKey, setHydratedDraftKey] = useState<string | null>(null);
  const { selection, userOverrides, selectionFrozen } = draftState;
  const [profilElementType, setProfilElementType] = useState<string | null>(
    null,
  );
  const [mdsFilters, setMdsFilters] = useState<MdsFilterState>({
    fastenerGroup: null,
    feederTracks: null,
    forceKn: null,
  });
  const [customToolingText, setCustomToolingText] = useState("");

  // True once the user makes a real edit (options / qty / profil type / MDS
  // filters / custom tooling). The auto-selection persisted on open does NOT
  // flip it, so opening + closing with no change reports `changed: false`.
  const dirtyRef = useRef(false);
  const markDirty = () => {
    dirtyRef.current = true;
  };
  const handleClose = () => onClose(dirtyRef.current);

  // The live `requiredMachines` re-scale effect below is driven BY the
  // comparison cost recompute. If its selection write also fired a compare-cost
  // invalidate, that would re-trigger the recompute → new requiredMachines →
  // re-scale → invalidate, an unbounded loop that spams calculate /
  // configure-options until the value converges (429 Too Many Requests). This
  // ref lets the re-scale persist to localStorage WITHOUT invalidating; user
  // edits, on-open hydration and the MSPe toggle still invalidate normally.
  const suppressCompareInvalidateRef = useRef(false);
  const lastCompareDraftSignatureRef = useRef<string | null>(null);

  // Manual ("custom") line items — POC parity: added locally in the popup,
  // rendered as configured cards, retained in the browser workspace draft, and
  // folded into the downloaded quotation / Word and the RFQ snapshot on submit.
  const [manualItems, setManualItems] = useState<DocManualLineItem[]>([]);
  const [manualDraft, setManualDraft] =
    useState<ManualDraft>(EMPTY_MANUAL_DRAFT);
  const [showManualForm, setShowManualForm] = useState(false);

  // D4 — when Configure is opened FROM a task (D2 sets `taskContext.taskId`),
  // manual line items live on the task: add → POST, remove → DELETE
  // `/v1/tasks/:id/manual-line-items` (POC ``). The
  // server list is the source of truth; `manualItemIds[i]` aligns with
  // `manualItems[i]` so the index-based card list can delete by id. With no
  // task context the items stay local (the RFQ/ad-hoc path is unchanged).
  const taskId = taskContext?.taskId ?? null;

  // PENN-095: the two document buttons read PREVIEW while the task's quote is not
  // yet finalized, and DOWNLOAD once it has been. Finalize Quote is itself gated
  // on Engineering + Sales approval (see TaskSidePanel / svc-06), so a finalized
  // task implies both approvals are complete. With no task context (ad-hoc
  // compare flow) the render is a never-persisted preview, so the Preview labels
  // stay. (React Query dedupes this `useTask` with TaskSidePanel's.)
  const { data: quoteTaskDetail } = useTask(taskId);
  const isQuoteFinalized =
    !!quoteTaskDetail &&
    (!!quoteTaskDetail.quotedAt ||
      quoteTaskDetail.workflowQuoteStatus === "quoted" ||
      quoteTaskDetail.status === "quoted");
  const quotationDocLabel = isQuoteFinalized
    ? "Download Quotation"
    : "Preview Quotation";
  const wordDocLabel = isQuoteFinalized
    ? "Download Word"
    : "Preview in Word format";

  const taskItemsQuery = useManualLineItems(taskId, open && !!taskId);
  const addTaskItem = useAddManualLineItem();
  const deleteTaskItem = useDeleteManualLineItem();
  const [manualItemIds, setManualItemIds] = useState<(string | null)[]>([]);

  useEffect(() => {
    if (!open || !taskId) return;
    const items = taskItemsQuery.data;
    if (!items) return;
    setManualItems(items.map(taskItemToDoc));
    setManualItemIds(items.map((i) => i.itemId));
  }, [open, taskId, taskItemsQuery.data]);

  // First-load hydration tracker — reruns on (productId | family) so reopening
  // for a different product seeds correctly.
  const hydratedKeyRef = useRef<string | null>(null);
  // Last `requiredMachines` we auto-selected against. Lets us re-scale the
  // machine line live when the comparison preview recomputes utilization while
  // the modal stays open (POC: machine qty follows `summary.requiredMachines`).
  const lastReqMachinesRef = useRef<number | null>(null);

  useEffect(() => {
    if (!open || !response || !response.available) return;
    const hydrationKey = `${productId}::${family ?? "none"}`;
    if (hydratedKeyRef.current === hydrationKey) return;
    hydratedKeyRef.current = hydrationKey;

    // D2: opening from a task seeds part numbers + quantities from the snapshot
    // and bypasses localStorage + family auto-select — the frozen snapshot is
    // the source of truth (POC `configState.selectedParts = m`).
    const hasFrozenSelection =
      initialSelectedOptions !== undefined ||
      initialSelectedPartNos !== undefined;
    const restored = hasFrozenSelection
      ? new Map<string, number>(
          initialSelectedOptions !== undefined
            ? initialSelectedOptions.map((o) => [o.partNo, o.qty ?? 1])
            : (initialSelectedPartNos ?? []).map((partNo) => [partNo, 1]),
        )
      : null;
    const stored = restored ? null : loadProductSelection(productId);
    const seeded =
      restored ??
      (stored ? itemsToSelectionMap(stored.items) : new Map<string, number>());
    const nextUserOverrides = overridesToSelectionMap(stored?.userOverrides);
    setCustomToolingText(
      restored
        ? (initialCustomToolingText ?? "")
        : (stored?.customToolingText ?? ""),
    );
    const nextProfilType =
      stored?.profilElementType ??
      response.meta.profilElementTypeDefault ??
      null;
    setProfilElementType(nextProfilType);

    const nextMdsFilters =
      family === "mds" && response.meta.mdsFilters
        ? {
            fastenerGroup:
              stored?.mdsFastenerGroup ??
              response.meta.mdsFilters.fastenerGroup,
            feederTracks:
              stored?.mdsFeederTracks ?? response.meta.mdsFilters.feederTracks,
            forceKn: stored?.mdsForceKn ?? response.meta.mdsFilters.forceKn,
          }
        : { fastenerGroup: null, feederTracks: null, forceKn: null };
    setMdsFilters(nextMdsFilters);

    const selectablePartNos = configureSelectablePartNos({
      response,
      family,
      profilElementType: nextProfilType,
      mdsFilters: nextMdsFilters,
    });
    const frozen = restored !== null || stored?.selectionFrozen === true;
    const recommended = family
      ? applyAutoSelect({
          response,
          family,
          mspAutoFeedEnabled,
          productId,
          region,
          requiredMachines,
          mdsFilters: nextMdsFilters,
          profilElementType: nextProfilType,
          existing: new Map(),
        }).selection
      : new Map<string, number>();
    const nextSelection = frozen
      ? new Map([...seeded].filter(([partNo]) => selectablePartNos.has(partNo)))
      : applyUserSelectionOverrides(
          recommended,
          nextUserOverrides,
          selectablePartNos,
        );
    setDraftState({
      selection: nextSelection,
      userOverrides: nextUserOverrides,
      selectionFrozen: frozen,
    });
    if (!taskId) {
      const currentCurrency = response.meta.currencyCode ?? "EUR";
      setManualItems(
        manualLineItemsForCurrency(stored?.manualLineItems, currentCurrency),
      );
    }
    setHydratedDraftKey(hydrationKey);
    lastReqMachinesRef.current = requiredMachines;
  }, [
    open,
    response,
    productId,
    family,
    region,
    requiredMachines,
    mspAutoFeedEnabled,
    initialSelectedOptions,
    initialSelectedPartNos,
    initialCustomToolingText,
    taskId,
  ]);

  // Live `requiredMachines` refresh (cross-cutting parity fix): the comparison
  // preview can recompute utilization (e.g. app↔machine assignment change)
  // while the modal stays open. Rebuild the family recommendation, then apply
  // explicit user overrides last, so untouched machine/family multipliers track
  // the new value without replacing edited rows. Guarded so it only fires on an
  // actual change after the initial hydration.
  useEffect(() => {
    if (!open || !response || !response.available || !family || selectionFrozen)
      return;
    if (hydratedKeyRef.current === null) return;
    if (lastReqMachinesRef.current === null) {
      lastReqMachinesRef.current = requiredMachines;
      return;
    }
    if (lastReqMachinesRef.current === requiredMachines) return;
    lastReqMachinesRef.current = requiredMachines;
    // Re-scale is compare-driven — persist it but don't echo an invalidate back.
    suppressCompareInvalidateRef.current = true;
    setDraftState((prev) => {
      const recommended = applyAutoSelect({
        response,
        family,
        mspAutoFeedEnabled,
        productId,
        region,
        requiredMachines,
        mdsFilters,
        profilElementType,
        existing: new Map(),
      }).selection;
      const selectablePartNos = configureSelectablePartNos({
        response,
        family,
        profilElementType,
        mdsFilters,
      });
      return {
        ...prev,
        selection: applyUserSelectionOverrides(
          recommended,
          prev.userOverrides,
          selectablePartNos,
        ),
      };
    });
  }, [
    requiredMachines,
    open,
    response,
    family,
    productId,
    region,
    mspAutoFeedEnabled,
    mdsFilters,
    profilElementType,
    selectionFrozen,
  ]);

  // Reset hydration key on close so the next open re-hydrates from disk.
  useEffect(() => {
    if (!open) {
      hydratedKeyRef.current = null;
      setHydratedDraftKey(null);
      lastReqMachinesRef.current = null;
      lastCompareDraftSignatureRef.current = null;
      // The requirements text stays on the persisted entry (D5) so the next
      // open re-hydrates it and an RFQ/quote raised later still carries it.
      setCustomToolingText("");
      setManualItems([]);
      setManualItemIds([]);
      setManualDraft(EMPTY_MANUAL_DRAFT);
      setShowManualForm(false);
    }
  }, [open, productId]);

  // Prevent page scroll behind modal (POC fixed overlay).
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // MSPe toggle change after initial hydration: POC `applyMspAutoFeedConfigSelection`.
  const lastMspAutoFeedRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (!open) {
      lastMspAutoFeedRef.current = null;
      return;
    }
    if (!response || !response.available || family !== "heger_pem") return;
    if (lastMspAutoFeedRef.current === null) {
      lastMspAutoFeedRef.current = mspAutoFeedEnabled;
      return;
    }
    if (lastMspAutoFeedRef.current === mspAutoFeedEnabled) return;
    lastMspAutoFeedRef.current = mspAutoFeedEnabled;

    setDraftState((prev) => {
      const synced = syncMspAutoFeedSelection({
        response,
        selection: prev.selection,
        mspAutoFeedEnabled,
        productId,
        region,
        requiredMachines,
      });
      if (prev.selectionFrozen) return { ...prev, selection: synced };

      const nextOverrides = new Map(prev.userOverrides);
      for (const partNo of mspAutoFeedManagedPartNos(response, productId)) {
        nextOverrides.delete(partNo);
      }
      const selectablePartNos = configureSelectablePartNos({
        response,
        family,
        profilElementType,
        mdsFilters,
      });
      return {
        ...prev,
        selection: applyUserSelectionOverrides(
          synced,
          nextOverrides,
          selectablePartNos,
        ),
        userOverrides: nextOverrides,
      };
    });
  }, [
    open,
    response,
    family,
    mspAutoFeedEnabled,
    requiredMachines,
    productId,
    region,
    profilElementType,
    mdsFilters,
  ]);

  const currency = response?.meta.currencyCode ?? "EUR";

  // Filter catalog for Profil (only show rows for the picked elementType).
  const catalogItems = useMemo(
    () =>
      configureCatalogItems({
        response,
        family,
        profilElementType,
        mdsFilters,
      }),
    [response, family, profilElementType, mdsFilters],
  );

  const selectablePartNos = useMemo(
    () =>
      configureSelectablePartNos({
        response,
        family,
        profilElementType,
        mdsFilters,
      }),
    [response, family, profilElementType, mdsFilters],
  );

  const mspFrequencyPartNos = useMemo(
    () =>
      response?.available && family === "heger_pem"
        ? mspAutoFeedUpgradePartNos(response, productId)
        : new Set<string>(),
    [response, family, productId],
  );

  const catalogGroups = useMemo(
    () => buildCatalogGroups(catalogItems),
    [catalogItems],
  );

  const customToolingItem = useMemo(() => getCustomToolingItem(), []);

  const unitPriceByPartNo = useMemo(
    () => configureUnitPrices({ response, catalogItems }),
    [response, catalogItems],
  );

  // POC `getSelectedConfigItemsWithQty()` only writes rows still present in the
  // active configure catalog. Apply the same boundary when a task View becomes
  // the current product draft: historical part numbers stay frozen on the task
  // but cannot leak into a later ad-hoc quote as unknown/dash-priced options.
  const taskDraftPartNos = useMemo(() => {
    if (!taskId || !response?.available) return null;
    const partNos = new Set(catalogItems.map((item) => item.partNo));
    for (const group of response.toolingGroups ?? []) {
      for (const item of group.items) partNos.add(item.partNo);
    }
    partNos.add(CUSTOM_TOOLING_PART_NO);
    return partNos;
  }, [taskId, response, catalogItems]);

  const total = useMemo(() => {
    let acc = 0;
    let any = false;
    for (const [partNo, qty] of selection) {
      if (qty <= 0) continue;
      const price = unitPriceByPartNo.get(partNo);
      if (price == null) continue;
      acc += price * qty;
      any = true;
    }
    return { acc, any };
  }, [selection, unitPriceByPartNo]);

  // Manual line items roll-up + combined grand total (options + manual).
  const manualTotal = useMemo(() => {
    let acc = 0;
    let any = false;
    for (const m of manualItems) {
      const unit = parseDecimalString(m.price);
      if (unit == null) continue;
      acc += unit * (m.qty ?? 1);
      any = true;
    }
    return { acc, any };
  }, [manualItems]);

  const grandTotal = {
    acc: total.acc + manualTotal.acc,
    any: total.any || manualTotal.any,
  };

  // Manual quote lines do not change utilization, labour, or quality, so keep
  // them out of the Calculate invalidation signature. Workspace-line add/remove
  // still marks the popup dirty; Configure-close then re-runs Optimize with the
  // revised one-time investment when optimization is already in play.
  const compareDraftSignature = useMemo(
    () =>
      JSON.stringify({
        selection: [...selection].sort(([a], [b]) => a.localeCompare(b)),
        profilElementType,
        customToolingText,
        mdsFilters,
      }),
    [selection, profilElementType, customToolingText, mdsFilters],
  );

  // Persist on every selection change so the quote and the display-only
  // "Recommended machine configuration" total use the same selected catalog
  // rows. The cost-engine card override remains independently auto-selected.
  useEffect(() => {
    if (!open || !response || !response.available) return;
    const activeDraftKey = `${productId}::${family ?? "none"}`;
    // Hydration and persistence are passive effects from the same render. Wait
    // for the hydrated state commit so a cached response cannot briefly persist
    // the pre-hydration empty draft and dispatch a redundant recalculation.
    if (hydratedDraftKey !== activeDraftKey) return;
    // §D.5 / §D.11 (POC task View parity): viewing a task intentionally makes
    // that exact configuration the current per-product browser draft. A later
    // normal Configure open retains the frozen task rows until the workspace is
    // reset; removed catalog rows are still filtered out.
    const draftSelection = taskDraftPartNos
      ? new Map(
          [...selection].filter(([partNo]) => taskDraftPartNos.has(partNo)),
        )
      : selection;
    const persistedManualLineItems = taskId
      ? []
      : replaceManualLineItemsForCurrency(
          loadProductSelection(productId)?.manualLineItems,
          currency,
          manualItems,
        );
    const payload: ConfigureProductSelection = {
      items: selectionMapToItems(draftSelection, unitPriceByPartNo),
      ...(userOverrides.size > 0
        ? {
            userOverrides: selectionOverridesToItems(
              userOverrides,
              selectablePartNos,
            ),
          }
        : {}),
      ...(selectionFrozen ? { selectionFrozen: true } : {}),
      ...(!taskId && persistedManualLineItems.length > 0
        ? { manualLineItems: persistedManualLineItems }
        : {}),
      ...(total.any && total.acc > 0 ? { optionsTotal: total.acc } : {}),
      ...(profilElementType ? { profilElementType } : {}),
      ...(customToolingText.trim()
        ? { customToolingText: customToolingText.trim() }
        : {}),
      ...(mdsFilters.fastenerGroup
        ? { mdsFastenerGroup: mdsFilters.fastenerGroup }
        : {}),
      ...(mdsFilters.feederTracks
        ? { mdsFeederTracks: mdsFilters.feederTracks }
        : {}),
      ...(mdsFilters.forceKn ? { mdsForceKn: mdsFilters.forceKn } : {}),
    };
    saveProductSelection(productId, payload);
    const compareChanged =
      lastCompareDraftSignatureRef.current !== compareDraftSignature;
    lastCompareDraftSignatureRef.current = compareDraftSignature;
    if (!compareChanged) {
      // A compare-driven required-machine refresh can resolve to the same
      // selection when the user pinned every affected quantity. Consume the
      // one-shot suppression here so it cannot swallow the next real edit.
      suppressCompareInvalidateRef.current = false;
      return;
    }
    // Skip exactly one invalidate for a compare-driven re-scale (see
    // `suppressCompareInvalidateRef`); every other selection change (user edit,
    // hydration, MSPe toggle) still invalidates the comparison cost.
    if (suppressCompareInvalidateRef.current) {
      suppressCompareInvalidateRef.current = false;
    } else {
      dispatchCompareCostInvalidate();
    }
  }, [
    open,
    response,
    productId,
    family,
    hydratedDraftKey,
    taskId,
    taskDraftPartNos,
    selection,
    userOverrides,
    selectionFrozen,
    selectablePartNos,
    manualItems,
    currency,
    profilElementType,
    customToolingText,
    mdsFilters,
    unitPriceByPartNo,
    total.acc,
    total.any,
    compareDraftSignature,
  ]);

  if (!open) return null;

  const toggleItem = (partNo: string, defaultQty = 1) => {
    markDirty();
    setDraftState((prev) => {
      const next = new Map(prev.selection);
      const selecting = !next.has(partNo);
      if (!selecting) {
        next.delete(partNo);
      } else {
        // 50 Hz and 60 Hz are alternatives, not additive options. Selecting one
        // is itself an explicit rejection of every sibling frequency row.
        if (mspFrequencyPartNos.has(partNo)) {
          for (const sibling of mspFrequencyPartNos) next.delete(sibling);
        }
        next.set(partNo, defaultQty);
      }
      if (prev.selectionFrozen) return { ...prev, selection: next };
      const nextOverrides = new Map(prev.userOverrides);
      if (selecting && mspFrequencyPartNos.has(partNo)) {
        for (const sibling of mspFrequencyPartNos) {
          if (sibling !== partNo) nextOverrides.set(sibling, 0);
        }
      }
      nextOverrides.set(partNo, next.get(partNo) ?? 0);
      return {
        ...prev,
        selection: next,
        userOverrides: nextOverrides,
      };
    });
  };
  const setQty = (partNo: string, qty: number) => {
    markDirty();
    setDraftState((prev) => {
      const next = new Map(prev.selection);
      if (qty <= 0) next.delete(partNo);
      else next.set(partNo, qty);
      if (prev.selectionFrozen) return { ...prev, selection: next };
      const nextOverrides = new Map(prev.userOverrides);
      nextOverrides.set(partNo, Math.max(0, Math.floor(qty)));
      return {
        ...prev,
        selection: next,
        userOverrides: nextOverrides,
      };
    });
  };

  const selectedPartNos = Array.from(selection.entries())
    .filter(([, qty]) => qty > 0)
    .map(([partNo]) => partNo);

  // Build the frozen option rows (snapshot/quote shape) from the live selection
  // + catalog metadata. Shared by RFQ submit, the ad-hoc quotation download and
  // the RFQ popup's own preview when it is opened without this modal.
  const buildSelectedOptions = (): SnapshotSelectedOptionInput[] =>
    buildSnapshotSelectedOptions({
      response,
      family,
      selection,
      profilElementType,
      mdsFilters,
      customToolingText,
    });

  const buildPayload = (): ConfigurePayload => ({
    selectedPartNos,
    selectedOptions: buildSelectedOptions(),
    manualLineItems: manualItems,
  });

  const addManualItem = () => {
    const description = manualDraft.description.trim();
    const priceNum = parseDecimalString(manualDraft.price);
    const qtyNum = Math.max(1, Math.floor(Number(manualDraft.qty) || 1));
    if (!description || priceNum == null || priceNum < 0) return;

    // D4 — task-bound: persist to the task; the list query re-syncs the cards.
    if (taskId) {
      addTaskItem.mutate({
        taskId,
        body: {
          description,
          partNo: manualDraft.partNo.trim() || null,
          category: manualDraft.category || null,
          itemName: manualDraft.itemName.trim() || description,
          partName: manualDraft.partName.trim() || null,
          machineType: manualDraft.machineType.trim() || null,
          toolingType: manualDraft.toolingType.trim() || null,
          qty: qtyNum,
          price: String(priceNum),
          currency,
        },
      });
      setManualDraft(EMPTY_MANUAL_DRAFT);
      setShowManualForm(false);
      return;
    }

    markDirty();
    setManualItems((prev) => [
      ...prev,
      {
        description,
        partNo: manualDraft.partNo.trim() || null,
        category: manualDraft.category || null,
        itemName: manualDraft.itemName.trim() || description,
        qty: qtyNum,
        price: String(priceNum),
        currency,
      },
    ]);
    setManualDraft(EMPTY_MANUAL_DRAFT);
    setShowManualForm(false);
  };

  const removeManualItem = (index: number) => {
    // D4 — task-bound: delete on the server (the list query re-syncs the cards).
    if (taskId) {
      const itemId = manualItemIds[index];
      if (itemId) deleteTaskItem.mutate({ taskId, itemId });
      return;
    }
    if (!manualItems[index]) return;
    markDirty();
    setManualItems((prev) => prev.filter((_, i) => i !== index));
  };

  const optionsHint =
    response?.meta.labels.optionsHint ??
    "Select options and quantities for this solution.";

  const hasAnyConfigured = selectedPartNos.length > 0 || manualItems.length > 0;

  return (
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden
      />
      <div className="absolute inset-0 flex items-center justify-center p-4 overflow-y-auto">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="configure-dialog-title"
          className="relative my-auto w-full max-w-none md:w-[min(96vw,108rem)] flex flex-col lg:flex-row items-stretch gap-4"
          onClick={(e) => e.stopPropagation()}
        >
          {/* POC #configModalCard */}
          <div className="@container w-full md:flex-1 min-w-0 rounded-3xl bg-white shadow-soft border border-slate-200 overflow-hidden flex flex-col max-h-[min(92vh,960px)]">
            <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-slate-200 flex-shrink-0">
              <div className="min-w-0">
                <div className="text-xs uppercase text-slate-500">
                  {response?.meta.labels.title ?? "Configure solution"}
                </div>
                <div className="mt-1">
                  <h2
                    id="configure-dialog-title"
                    className="text-xl md:text-2xl font-semibold text-slate-900 truncate"
                  >
                    {product.name}
                  </h2>
                </div>
                <p className="mt-1 text-sm text-slate-600">
                  {response?.meta.labels.subtitle ?? "Machine options"}
                </p>
                {appSummary && (
                  <div className="mt-1.5">
                    <p className="text-sm font-medium text-slate-700">
                      Applications: {appSummary.applications.length} • Element
                      types: {appSummary.elementTypes.length}
                    </p>
                    {(appSummary.applications.length > 0 ||
                      appSummary.elementTypes.length > 0) && (
                      <p className="mt-0.5 text-xs text-slate-500 truncate">
                        {appSummary.applications.length > 0 && (
                          <>Apps: {appSummary.applications.join(", ")}</>
                        )}
                        {appSummary.elementTypes.length > 0 && (
                          <> · Elements: {appSummary.elementTypes.join(", ")}</>
                        )}
                      </p>
                    )}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="h-10 w-10 flex-shrink-0 rounded-xl border border-slate-300 bg-white text-slate-700 shadow-sm hover:bg-slate-50"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {/* POC #configGrid — 2/3 options + 1/3 summary */}
            <div className="grid grid-cols-1 @2xl:grid-cols-3 min-h-0 flex-1 overflow-hidden">
              <div className="@2xl:col-span-2 px-6 py-5 flex flex-col min-h-0 min-w-0">
                {family === "profil_indi" &&
                  response?.available &&
                  response.meta.profilElementTypes && (
                    <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 flex-shrink-0">
                      <label className="text-xs font-semibold uppercase text-slate-500">
                        Element type
                      </label>
                      <select
                        className="mt-2 h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-800"
                        value={profilElementType ?? ""}
                        onChange={(e) => {
                          markDirty();
                          setProfilElementType(e.target.value);
                        }}
                      >
                        {response.meta.profilElementTypes.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                      <p className="mt-2 text-xs text-slate-500">
                        Only options with a price for the selected element type
                        are shown.
                      </p>
                    </div>
                  )}

                {family === "mds" &&
                  response?.available &&
                  response.meta.mdsFilters && (
                    <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 flex-shrink-0">
                      <div className="text-xs font-semibold uppercase text-slate-500">
                        Filters
                      </div>
                      <div className="mt-3 grid grid-cols-1 @2xl:grid-cols-3 gap-3">
                        <MdsFilterSelect
                          label="Fastener group"
                          value={mdsFilters.fastenerGroup}
                          options={
                            response.meta.mdsFilters.allowed.fastenerGroup
                          }
                          onChange={(value) => {
                            markDirty();
                            setMdsFilters((prev) => ({
                              ...prev,
                              fastenerGroup: value,
                            }));
                          }}
                        />
                        <MdsFilterSelect
                          label="Feeder tracks"
                          value={mdsFilters.feederTracks}
                          options={
                            response.meta.mdsFilters.allowed.feederTracks
                          }
                          onChange={(value) => {
                            markDirty();
                            setMdsFilters((prev) => ({
                              ...prev,
                              feederTracks: value,
                            }));
                          }}
                        />
                        <MdsFilterSelect
                          label="Force (kN)"
                          value={mdsFilters.forceKn}
                          options={response.meta.mdsFilters.allowed.forceKn}
                          onChange={(value) => {
                            markDirty();
                            setMdsFilters((prev) => ({
                              ...prev,
                              forceKn: value,
                            }));
                          }}
                        />
                      </div>
                      <p className="mt-2 text-xs text-slate-500">
                        {response.meta.labels.optionsHint}
                      </p>
                    </div>
                  )}

                <div className="flex items-center justify-between gap-3 flex-shrink-0">
                  <div className="text-sm font-semibold text-slate-900">
                    Available machine options
                  </div>
                  <div className="text-xs text-slate-500 text-right">
                    {optionsHint}
                  </div>
                </div>

                {/* POC #configOptions — scroll lives here */}
                <div className="mt-4 min-h-0 flex-1 overflow-y-auto max-h-[50vh] pr-2 overscroll-contain">
                  <BodyContent
                    query={query}
                    response={response}
                    catalogGroups={catalogGroups}
                    toolingGroups={response?.toolingGroups}
                    selection={selection}
                    onToggle={toggleItem}
                    onQtyChange={setQty}
                    canShowPrices={canShowPrices}
                    isAuthenticated={isAuthenticated}
                    currency={currency}
                    family={family}
                    emptyMessage={response?.meta.labels.emptyMessage}
                    toolingUnavailableMessage={
                      response?.meta.labels.toolingUnavailableMessage
                    }
                    customToolingItem={customToolingItem}
                    customToolingText={customToolingText}
                    onCustomToolingTextChange={(v) => {
                      markDirty();
                      setCustomToolingText(v);
                    }}
                  />
                </div>
              </div>

              <div className="border-t @2xl:border-t-0 @2xl:border-l border-slate-200 px-6 py-5 bg-slate-50/60 flex flex-col min-h-0 overflow-y-auto">
                <div className="text-sm font-semibold text-slate-900">
                  Configuration summary
                </div>

                <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 flex-shrink-0">
                  {/* POC parity (.priceField → display:none for no-price users):
                      hide the total value entirely and show only the note —
                      never render an em-dash placeholder. */}
                  {canShowPrices ? (
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                      <div className="text-sm text-slate-600">
                        Selected options total
                      </div>
                      <div className="text-xl font-semibold text-slate-900">
                        {grandTotal.any
                          ? formatMoney(
                              String(grandTotal.acc.toFixed(4)),
                              currency,
                            )
                          : formatMoney("0", currency)}
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-600">
                      You don&apos;t have permission to view prices.
                    </p>
                  )}
                </div>

                {/* CONFIGURED ITEMS — POC parity (screenshot): each selected
                    option and manual line item rendered as its own card. The
                    block keeps its natural size (flex-shrink-0) and the inner
                    list scrolls; without this the list could grow under the
                    action buttons and overlap them once items are added. */}
                <div className="mt-4 flex-shrink-0">
                  <div className="text-xs font-semibold uppercase text-slate-500">
                    Selected options
                  </div>
                  {!hasAnyConfigured ? (
                    <p className="mt-2 text-sm text-slate-600">None selected</p>
                  ) : (
                    <ConfiguredItemsList
                      response={response}
                      catalogItems={catalogItems}
                      selection={selection}
                      manualItems={manualItems}
                      onRemoveManualItem={removeManualItem}
                      canShowPrices={canShowPrices}
                      currency={currency}
                      customToolingText={customToolingText}
                    />
                  )}
                </div>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2 flex-shrink-0">
                  {isAuthenticated && canGenerateQuote && (
                    <>
                      <PennButton
                        type="button"
                        variant="primary"
                        size="md"
                        fullWidth
                        onClick={() => onDownloadQuotation?.(buildPayload())}
                      >
                        {quotationDocLabel}
                      </PennButton>
                      <PennButton
                        type="button"
                        variant="secondary"
                        size="md"
                        fullWidth
                        onClick={() => onDownloadWord?.(buildPayload())}
                      >
                        {wordDocLabel}
                      </PennButton>
                    </>
                  )}

                  {isAuthenticated && canGenerateQuote && (
                    <PennButton
                      type="button"
                      variant="secondary"
                      size="md"
                      fullWidth
                      className="sm:col-span-2"
                      onClick={() => setShowManualForm(true)}
                    >
                      Add manual line item
                    </PennButton>
                  )}

                  {showManualForm && (
                    <ManualLineItemForm
                      draft={manualDraft}
                      currency={currency}
                      canShowPrices={canShowPrices}
                      canSaveToLibrary={canGenerateQuote}
                      onChange={setManualDraft}
                      onAdd={addManualItem}
                      onCancel={() => {
                        setShowManualForm(false);
                        setManualDraft(EMPTY_MANUAL_DRAFT);
                      }}
                    />
                  )}

                  <PennButton
                    type="button"
                    variant="secondary"
                    size="md"
                    fullWidth
                    className="sm:col-span-2"
                    onClick={() => onRequestQuotation?.(buildPayload())}
                  >
                    Request Quotation / Support
                  </PennButton>
                </div>

                {!isAuthenticated && (
                  <p className="mt-3 text-xs text-slate-500 flex-shrink-0">
                    Sign in to request a quotation.
                  </p>
                )}
              </div>
            </div>
          </div>

          {taskContext && canWorkForOthers && (
            <TaskSidePanel task={taskContext} />
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface BodyContentProps {
  query: ReturnType<typeof useConfigureOptions>;
  response: ConfigureOptionsResponse | undefined;
  catalogGroups: CatalogGroup[];
  toolingGroups: ToolingGroupDto[] | undefined;
  selection: Map<string, number>;
  onToggle: (partNo: string, defaultQty?: number) => void;
  onQtyChange: (partNo: string, qty: number) => void;
  canShowPrices: boolean;
  isAuthenticated: boolean;
  currency: string;
  family: ConfigureFamily | null;
  emptyMessage: string | undefined;
  /** Copy for a machine no tooling is mapped to. */
  toolingUnavailableMessage: string | undefined;
  customToolingItem: ConfigureOptionDto;
  customToolingText: string;
  onCustomToolingTextChange: (text: string) => void;
}

const BodyContent = ({
  query,
  response,
  catalogGroups,
  toolingGroups,
  selection,
  onToggle,
  onQtyChange,
  canShowPrices,
  isAuthenticated,
  currency,
  family,
  emptyMessage,
  toolingUnavailableMessage,
  customToolingItem,
  customToolingText,
  onCustomToolingTextChange,
}: BodyContentProps) => {
  if (query.isPending && !response) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
        Loading options…
      </div>
    );
  }
  if (query.isError) {
    const rateLimitMessage = toCalculationRateLimitError(query.error)?.message;
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50/40 p-4 text-sm text-red-700">
        {rateLimitMessage ?? "Failed to load options. Please try again."}
      </div>
    );
  }
  if (response && !response.available) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
        {emptyMessage ??
          "No configurable options are available for this product."}
      </div>
    );
  }
  // Must NOT collapse into this message-only state: Custom Tooling stays
  // actionable, so the heger_pem section renders and carries the notice.
  if (
    catalogGroups.length === 0 &&
    (!toolingGroups || toolingGroups.length === 0) &&
    !toolingUnavailableMessage
  ) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
        {emptyMessage ?? "No configurable options match this region."}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {catalogGroups.map((group) => (
        <section key={group.category} className="mt-6 first:mt-0">
          {group.subgroups.map(({ subCategory, items }) => (
            <SubgroupBlock
              key={`${group.category}::${subCategory ?? ""}`}
              subCategory={subCategory}
              items={items}
              selection={selection}
              onToggle={onToggle}
              onQtyChange={onQtyChange}
              canShowPrices={canShowPrices}
              currency={currency}
            />
          ))}
        </section>
      ))}

      {family === "heger_pem" && (
        <section className="mt-6 first:mt-0">
          {/* Same box as the typed-unavailable notice above. Gated on
              `catalogGroups` (post-`catalogItemsForFamily`, so the actually
              rendered rows) being empty — a machine can own no tooling yet
              still have machine options, where the banner would contradict. */}
          {toolingUnavailableMessage && catalogGroups.length === 0 && (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              {toolingUnavailableMessage}
            </div>
          )}
          {(toolingGroups ?? []).map((group) => (
            <div key={group.fastenerPartNo} className="mt-4 first:mt-0">
              <div className="text-sm font-semibold text-slate-900">
                {group.fastenerLabel}
              </div>
              {group.items.length === 0 ? (
                <p className="mt-2 text-xs text-slate-500">
                  No priced tooling for {group.fastenerLabel}.
                </p>
              ) : (
                <div className="mt-2 space-y-2">
                  {sortOptionRows(group.items).map((item) => (
                    <OptionRow
                      key={item.partNo}
                      item={item}
                      qty={selection.get(item.partNo) ?? 0}
                      onToggle={onToggle}
                      onQtyChange={onQtyChange}
                      canShowPrices={canShowPrices}
                      currency={currency}
                      isTooling
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
          <CustomToolingRow
            item={customToolingItem}
            qty={selection.get(CUSTOM_TOOLING_PART_NO) ?? 0}
            text={customToolingText}
            onToggle={onToggle}
            onTextChange={onCustomToolingTextChange}
            canShowPrices={canShowPrices}
            currency={currency}
          />
        </section>
      )}

      {/* POC parity (renderConfigOptions L8730/L8811): the "Custom
          Tooling" placeholder is rendered UNCONDITIONALLY for every machine
          (POC force-adds a "Tooling" category and always appends it). heger_pem
          already shows it inside its fastener-grouped tooling section above, so
          here we render it for every OTHER family (mds, t_series, profil_indi,
          sherex, …). Previously this was `family === "mds"` only, which hid
          Custom Tooling on FLEX-5P / sherex / profil machines. */}
      {family !== "heger_pem" && (
        <section className="mt-6 first:mt-0">
          <CustomToolingRow
            item={customToolingItem}
            qty={selection.get(CUSTOM_TOOLING_PART_NO) ?? 0}
            text={customToolingText}
            onToggle={onToggle}
            onTextChange={onCustomToolingTextChange}
            canShowPrices={canShowPrices}
            currency={currency}
          />
        </section>
      )}

      {!isAuthenticated && (
        <p className="mt-4 text-xs text-slate-500">
          Sign in to save your configuration and download quotations.
        </p>
      )}
    </div>
  );
};

interface SubgroupBlockProps {
  subCategory: string | null;
  items: ConfigureOptionDto[];
  selection: Map<string, number>;
  onToggle: (partNo: string, defaultQty?: number) => void;
  onQtyChange: (partNo: string, qty: number) => void;
  canShowPrices: boolean;
  currency: string;
}

const SubgroupBlock = ({
  subCategory,
  items,
  selection,
  onToggle,
  onQtyChange,
  canShowPrices,
  currency,
}: SubgroupBlockProps) => (
  <div className="mt-4 first:mt-0">
    {subCategory && (
      <div className="text-xs font-semibold uppercase text-slate-500">
        {subCategory}
      </div>
    )}
    <div className="mt-2 space-y-2">
      {items.map((item) => (
        <OptionRow
          key={item.partNo}
          item={item}
          qty={selection.get(item.partNo) ?? 0}
          onToggle={onToggle}
          onQtyChange={onQtyChange}
          canShowPrices={canShowPrices}
          currency={currency}
        />
      ))}
    </div>
  </div>
);

interface OptionRowProps {
  item: ConfigureOptionDto;
  qty: number;
  onToggle: (partNo: string, defaultQty?: number) => void;
  onQtyChange: (partNo: string, qty: number) => void;
  canShowPrices: boolean;
  currency: string;
  isTooling?: boolean;
}

const OptionRow = ({
  item,
  qty,
  onToggle,
  onQtyChange,
  canShowPrices,
  currency,
  isTooling,
}: OptionRowProps) => {
  const checked = qty > 0;
  const title = optionDisplayTitle(item, isTooling);
  const showProperties =
    item.kind === "catalog" && (item.properties?.length ?? 0) > 0;

  return (
    <label className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-3 hover:bg-slate-50 cursor-pointer">
      <input
        type="checkbox"
        className="mt-1 h-4 w-4 flex-shrink-0 rounded border-slate-300 text-penn-500 focus:ring-penn-500"
        checked={checked}
        onChange={() => onToggle(item.partNo)}
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-nowrap items-start justify-between gap-x-3">
          <div className="min-w-0 flex-1 text-sm font-semibold text-slate-900 break-words [overflow-wrap:anywhere]">
            {title}
          </div>
          <div className="flex items-center gap-4 flex-shrink-0">
            <div className="flex items-center justify-end gap-2 w-24">
              <span
                className={`text-[10px] font-semibold uppercase text-slate-500 ${
                  checked ? "" : "invisible"
                }`}
              >
                QTY
              </span>
              <input
                type="number"
                min={1}
                step={1}
                value={qty}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  const next = Number(e.target.value);
                  if (Number.isFinite(next))
                    onQtyChange(item.partNo, Math.floor(next));
                }}
                className={`h-7 w-14 rounded-lg border border-slate-300 bg-white px-2 text-xs text-slate-800 focus:outline-none ${
                  checked ? "" : "invisible"
                }`}
                aria-label={`Quantity for ${title}`}
              />
            </div>
            {item.price != null && canShowPrices && (
              <div className="text-sm font-semibold text-slate-900 text-right min-w-[9.5rem]">
                {formatMoney(item.price, currency)}
              </div>
            )}
          </div>
        </div>
        <div className="mt-1 space-y-1">
          {!isTooling && item.partNo && (
            <div className="text-sm text-slate-600">{item.partNo}</div>
          )}
          {item.description && item.description !== title && !isTooling && (
            <div className="text-sm text-slate-600">{item.description}</div>
          )}
          {isTooling && item.partNo && (
            <div className="text-xs text-slate-500">
              Tooling No: <span className="font-semibold">{item.partNo}</span>
              {item.subCategory ? (
                <>
                  {" "}
                  · Tooling type:{" "}
                  <span className="font-semibold">{item.subCategory}</span>
                </>
              ) : null}
            </div>
          )}
          {item.elementType && (
            <div className="text-xs text-slate-500">
              Element type:{" "}
              <span className="font-semibold">{item.elementType}</span>
            </div>
          )}
          {showProperties &&
            item.properties?.map((prop) => (
              <div
                key={`${item.partNo}::${prop.key}`}
                className="text-xs text-slate-600"
              >
                <span className="font-semibold text-slate-700">
                  {prop.label || prop.key}:
                </span>{" "}
                {prop.value ?? ""}
              </div>
            ))}
        </div>
      </div>
    </label>
  );
};

const CustomToolingRow = ({
  item,
  qty,
  text,
  onToggle,
  onTextChange,
  canShowPrices,
  currency,
}: {
  item: ConfigureOptionDto;
  qty: number;
  text: string;
  onToggle: (partNo: string, defaultQty?: number) => void;
  onTextChange: (text: string) => void;
  canShowPrices: boolean;
  currency: string;
}) => {
  const checked = qty > 0;
  return (
    <div className="mt-6">
      <div className="text-sm font-semibold text-slate-900">Custom Tooling</div>
      <label className="mt-2 flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-3 hover:bg-slate-50 cursor-pointer">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 flex-shrink-0 rounded border-slate-300 text-penn-500 focus:ring-penn-500"
          checked={checked}
          onChange={() => onToggle(item.partNo, 1)}
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-nowrap items-start justify-between gap-x-3">
            <div className="min-w-0 flex-1 text-sm font-semibold text-slate-900 break-words [overflow-wrap:anywhere]">
              Custom Tooling
            </div>
            {canShowPrices && (
              <div className="text-sm font-semibold text-slate-900 text-right min-w-[9.5rem]">
                {formatMoney(item.price ?? "0", currency)}
              </div>
            )}
          </div>
          <p className="mt-1 text-sm text-slate-600">
            Use this placeholder to describe requested custom tooling. During
            engineering review this can be replaced with concrete custom
            options.
          </p>
          {checked && (
            <textarea
              className="customToolingText mt-2 w-full min-h-[96px] rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none"
              placeholder="Describe your custom tooling requirements..."
              maxLength={2000}
              value={text}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => onTextChange(e.target.value)}
            />
          )}
        </div>
      </label>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Configured items — card list (POC parity, screenshot "CONFIGURED ITEMS")
// ---------------------------------------------------------------------------

interface ConfiguredItemsListProps {
  response: ConfigureOptionsResponse | undefined;
  catalogItems: ConfigureOptionDto[];
  selection: Map<string, number>;
  manualItems: DocManualLineItem[];
  onRemoveManualItem: (index: number) => void;
  canShowPrices: boolean;
  currency: string;
  customToolingText: string;
}

const ConfiguredItemsList = ({
  response,
  catalogItems,
  selection,
  manualItems,
  onRemoveManualItem,
  canShowPrices,
  currency,
  customToolingText,
}: ConfiguredItemsListProps) => {
  // partNo → {item, fastener}. Tooling-group items carry the owning fastener so
  // the card can show "Fastener: …" like the POC.
  const lookup = useMemo(() => {
    const out = new Map<
      string,
      { item: ConfigureOptionDto; fastener: string | null }
    >();
    for (const item of catalogItems)
      out.set(item.partNo, { item, fastener: null });
    for (const group of response?.toolingGroups ?? []) {
      for (const item of group.items) {
        out.set(item.partNo, {
          item,
          fastener: group.fastenerLabel ?? group.fastenerPartNo ?? null,
        });
      }
    }
    out.set(CUSTOM_TOOLING_PART_NO, {
      item: getCustomToolingItem(),
      fastener: null,
    });
    return out;
  }, [catalogItems, response]);

  const rows = useMemo(() => {
    const out: Array<{
      item: ConfigureOptionDto;
      qty: number;
      fastener: string | null;
    }> = [];
    for (const [partNo, qty] of selection) {
      if (qty <= 0) continue;
      const found = lookup.get(partNo);
      if (found) out.push({ item: found.item, qty, fastener: found.fastener });
    }
    return out;
  }, [lookup, selection]);

  const fmt = (value: string | null | undefined): string =>
    canShowPrices && value != null ? formatMoney(value, currency) : "—";

  return (
    <div className="mt-2 space-y-2 max-h-[34vh] overflow-y-auto pr-1">
      {rows.map(({ item, qty, fastener }) => {
        const unit = parseDecimalString(item.price);
        const lineTotal = unit != null ? String((unit * qty).toFixed(4)) : null;
        const isTooling = item.subCategory != null || fastener != null;
        const isCustomTooling = item.partNo === CUSTOM_TOOLING_PART_NO;
        const title = item.itemName ?? item.partNo;
        const requirements = customToolingText.trim();
        return (
          <div
            key={item.partNo}
            className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm"
          >
            <div className="flex flex-nowrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1 text-sm font-semibold text-slate-900 break-words [overflow-wrap:anywhere]">
                {title}
              </div>
              {canShowPrices && (
                <div className="text-sm font-semibold text-slate-900 text-right flex-shrink-0">
                  {fmt(lineTotal)}
                </div>
              )}
            </div>
            {item.description && item.description !== title && (
              <div className="mt-0.5 text-xs text-slate-600">
                {item.description}
              </div>
            )}
            <div className="mt-1 text-xs text-slate-500 space-y-0.5">
              {isCustomTooling ? (
                <>
                  <div className="break-words">
                    {requirements ? (
                      <>
                        Requirements:{" "}
                        <span className="font-medium text-slate-700">
                          {requirements}
                        </span>
                      </>
                    ) : (
                      "No requirements entered yet."
                    )}
                  </div>
                  <div>
                    Placeholder for engineering review
                    {canShowPrices && (
                      <>
                        {" · "}Unit:{" "}
                        <span className="font-medium text-slate-700">
                          {fmt(item.price)}
                        </span>
                      </>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {isTooling ? (
                    <div>
                      Tooling No:{" "}
                      <span className="font-medium text-slate-700">
                        {item.partNo}
                      </span>
                      {item.subCategory ? (
                        <>
                          {" · "}Tooling type:{" "}
                          <span className="font-medium text-slate-700">
                            {item.subCategory}
                          </span>
                        </>
                      ) : null}
                    </div>
                  ) : (
                    <div>
                      Part No:{" "}
                      <span className="font-medium text-slate-700">
                        {item.partNo}
                      </span>
                    </div>
                  )}
                  {item.elementType && (
                    <div>
                      Element type:{" "}
                      <span className="font-medium text-slate-700">
                        {item.elementType}
                      </span>
                    </div>
                  )}
                  {fastener && (
                    <div>
                      Fastener:{" "}
                      <span className="font-medium text-slate-700">
                        {fastener}
                      </span>
                    </div>
                  )}
                  <div>
                    Qty:{" "}
                    <span className="font-medium text-slate-700">{qty}</span>
                    {canShowPrices && (
                      <>
                        {" · "}Unit:{" "}
                        <span className="font-medium text-slate-700">
                          {fmt(item.price)}
                        </span>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })}

      {manualItems.map((m, index) => {
        const unit = parseDecimalString(m.price);
        const lineTotal =
          unit != null ? String((unit * (m.qty ?? 1)).toFixed(4)) : null;
        return (
          <div
            key={`manual-${index}`}
            className="rounded-2xl border border-penn-200 bg-penn-50/40 p-3 shadow-sm"
          >
            <div className="flex flex-nowrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1 text-sm font-semibold text-slate-900 break-words [overflow-wrap:anywhere]">
                {m.itemName ?? m.description}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {canShowPrices && (
                  <div className="text-sm font-semibold text-slate-900 text-right">
                    {fmt(lineTotal)}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => onRemoveManualItem(index)}
                  className="h-6 w-6 rounded-lg border border-slate-300 bg-white text-slate-500 hover:bg-slate-100"
                  aria-label="Remove manual line item"
                  title="Remove"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="mt-1 text-xs text-slate-500 space-y-0.5">
              <div className="inline-flex items-center gap-1 rounded-full bg-penn-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-penn-700">
                Manual line item
              </div>
              {m.partNo && (
                <div>
                  Part No:{" "}
                  <span className="font-medium text-slate-700">{m.partNo}</span>
                </div>
              )}
              <div>
                Qty:{" "}
                <span className="font-medium text-slate-700">{m.qty ?? 1}</span>
                {canShowPrices && (
                  <>
                    {" · "}Unit:{" "}
                    <span className="font-medium text-slate-700">
                      {fmt(m.price)}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Add manual line item — inline form (POC "Add manual line item")
// ---------------------------------------------------------------------------

const MANUAL_FIELD_CLASS =
  "mt-1 h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-penn-300";
const MANUAL_LABEL_CLASS =
  "text-[11px] font-semibold uppercase text-slate-500";

const ManualLineItemForm = ({
  draft,
  currency,
  canShowPrices,
  canSaveToLibrary,
  onChange,
  onAdd,
  onCancel,
}: {
  draft: ManualDraft;
  currency: string;
  canShowPrices: boolean;
  canSaveToLibrary: boolean;
  onChange: (next: ManualDraft) => void;
  onAdd: () => void;
  onCancel: () => void;
}) => {
  // Reusable library (POC left pane). GET requires canGenerateQuote; fetched
  // once when the form mounts. The "save to library" write path is gated on
  // canGenerateQuote too (quote-capable staff — POC parity).
  const libraryQuery = useManualQuoteItems(true);
  const saveMutation = useSaveManualQuoteItem();
  const [search, setSearch] = useState("");

  const priceNum = parseDecimalString(draft.price);
  const qtyNum = Math.max(1, Math.floor(Number(draft.qty) || 1));
  const canAdd =
    draft.description.trim().length > 0 && priceNum != null && priceNum >= 0;
  // Show the region currency at 0.00 (e.g. "€0.00" / "$0.00") until a price is
  // typed — an empty/zero price is a real 0 line total, not "unknown". The
  // value is only rendered when `canShowPrices` (the row is hidden otherwise),
  // so no em-dash placeholder is ever shown.
  const lineTotal = formatMoney(
    String(((priceNum ?? 0) * qtyNum).toFixed(4)),
    currency,
  );

  const filteredLibrary = useMemo(() => {
    const libraryItems = libraryQuery.data ?? [];
    const q = search.trim().toLowerCase();
    const matches = !q
      ? libraryItems
      : libraryItems.filter((it) =>
          [it.label, it.partNo, it.description, it.category]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(q),
        );
    return [...matches].sort((a, b) =>
      String(a.label || a.description || "").localeCompare(
        String(b.label || b.description || ""),
      ),
    );
  }, [libraryQuery.data, search]);

  const pickLibraryItem = (it: ManualQuoteItem) => {
    onChange({
      ...draft,
      selectedLibraryId: it.id,
      category: normalizeManualSection(it.category),
      partNo: it.partNo ?? "",
      description: it.description ?? "",
      qty: String(it.qty ?? 1),
      price: it.price ?? "",
      label: it.label ?? "",
      itemName: it.itemName ?? "",
      partName: it.partName ?? "",
      machineType: it.machineType ?? "",
      toolingType: it.toolingType ?? "",
    });
  };

  const handleAdd = async () => {
    if (!canAdd) return;
    // Best-effort: save/update the reusable library (quote-capable staff), then
    // add the line to the quotation. A library failure must NOT block the add (POC).
    if (draft.saveToLibrary && canSaveToLibrary && priceNum != null) {
      try {
        await saveMutation.mutateAsync({
          id: draft.selectedLibraryId ?? undefined,
          body: {
            label: draft.label.trim() || null,
            partNo: draft.partNo.trim() || null,
            description: draft.description.trim(),
            category: draft.category || null,
            toolingType: draft.toolingType.trim() || null,
            machineType: draft.machineType.trim() || null,
            itemName: draft.itemName.trim() || null,
            partName: draft.partName.trim() || null,
            currency,
            price: String(priceNum),
            qty: qtyNum,
          },
        });
      } catch (err) {
        console.warn("Failed to save reusable library item", err);
      }
    }
    onAdd();
  };

  const saving = saveMutation.isPending;

  return (
    <div className="fixed inset-0 z-[130] flex items-start justify-center">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-black/40"
        onClick={onCancel}
      />
      <div className="relative mx-auto mt-12 flex max-h-[86vh] w-[min(980px,94vw)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
        {/* Header — POC: title + helper + close */}
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5">
          <div>
            <div className="text-lg font-semibold text-slate-900">
              Add manual line item
            </div>
            <p className="mt-1 text-sm text-slate-600">
              Add a quotation position that is not available in the configured
              list.
              {canSaveToLibrary
                ? " Reuse a saved item from the library, or enter a new one and optionally save it back."
                : " Reuse a saved item from the library, or enter a new one."}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Body — POC two-pane: Reusable library + Line item */}
        <div className="flex-1 overflow-auto p-5">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* LEFT — Reusable library */}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className={MANUAL_LABEL_CLASS}>Reusable library</div>
                <button
                  type="button"
                  onClick={() => libraryQuery.refetch()}
                  disabled={libraryQuery.isFetching}
                  className="h-8 rounded-xl border border-slate-300 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                >
                  {libraryQuery.isFetching ? "Refreshing…" : "Refresh"}
                </button>
              </div>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search library..."
                className="mt-3 h-9 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-penn-300"
                aria-label="Search reusable library"
              />

              {libraryQuery.isLoading ? (
                <div className="mt-3 text-sm text-slate-600">
                  Loading library…
                </div>
              ) : libraryQuery.isError ? (
                <div className="mt-3 text-sm text-rose-700">
                  Couldn&apos;t load the reusable library.
                </div>
              ) : filteredLibrary.length === 0 ? (
                <div className="mt-3 text-sm text-slate-600">
                  No reusable items found.
                </div>
              ) : (
                <div className="mt-3 max-h-[42vh] space-y-2 overflow-auto pr-1">
                  {filteredLibrary.map((it) => {
                    const title = it.label || it.description || "Manual item";
                    const unit =
                      canShowPrices && it.price != null
                        ? formatMoney(it.price, it.currency || currency)
                        : null;
                    const isSelected = draft.selectedLibraryId === it.id;
                    return (
                      <button
                        key={it.id}
                        type="button"
                        onClick={() => pickLibraryItem(it)}
                        className={`w-full rounded-2xl border bg-white p-3 text-left hover:bg-slate-50 ${
                          isSelected
                            ? "border-penn-400 ring-1 ring-penn-300"
                            : "border-slate-200"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-slate-900">
                              {title}
                              {it.partNo ? (
                                <span className="ml-1 text-xs font-semibold text-slate-500">
                                  ({it.partNo})
                                </span>
                              ) : null}
                            </div>
                            <div className="mt-1 text-xs text-slate-500">
                              Category:{" "}
                              <span className="font-semibold">
                                {normalizeManualSection(it.category)}
                              </span>
                            </div>
                          </div>
                          {unit ? (
                            <div className="shrink-0 text-xs font-semibold text-slate-700">
                              {unit}
                            </div>
                          ) : null}
                        </div>
                        {it.description ? (
                          <div className="mt-2 line-clamp-2 text-sm text-slate-600">
                            {it.description}
                          </div>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* RIGHT — Line item */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className={MANUAL_LABEL_CLASS}>Line item</div>

              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className={MANUAL_LABEL_CLASS}>Section</label>
                  <select
                    value={draft.category}
                    onChange={(e) =>
                      onChange({ ...draft, category: e.target.value })
                    }
                    className={MANUAL_FIELD_CLASS}
                  >
                    {MANUAL_SECTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={MANUAL_LABEL_CLASS}>
                    Part No. (optional)
                  </label>
                  <input
                    type="text"
                    value={draft.partNo}
                    onChange={(e) =>
                      onChange({ ...draft, partNo: e.target.value })
                    }
                    placeholder="e.g., CUSTOM-001"
                    className={MANUAL_FIELD_CLASS}
                  />
                </div>
              </div>

              <div className="mt-3">
                <label className={MANUAL_LABEL_CLASS}>Description</label>
                <textarea
                  rows={4}
                  value={draft.description}
                  onChange={(e) =>
                    onChange({ ...draft, description: e.target.value })
                  }
                  placeholder="Describe the manual item (scope, notes, assumptions)..."
                  className="mt-1 w-full rounded-xl border border-slate-300 bg-white p-3 text-sm text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-penn-300"
                />
              </div>

              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div>
                  <label className={MANUAL_LABEL_CLASS}>Quantity</label>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={draft.qty}
                    onChange={(e) =>
                      onChange({ ...draft, qty: e.target.value })
                    }
                    className={MANUAL_FIELD_CLASS}
                    aria-label="Quantity"
                  />
                </div>
                <div className={canShowPrices ? "" : "opacity-60"}>
                  <label className={MANUAL_LABEL_CLASS}>Unit price</label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={draft.price}
                    onChange={(e) =>
                      onChange({ ...draft, price: e.target.value })
                    }
                    placeholder="0.00"
                    className={MANUAL_FIELD_CLASS}
                    aria-label="Unit price"
                  />
                </div>
                <div>
                  <label className={MANUAL_LABEL_CLASS}>Currency</label>
                  <input
                    type="text"
                    readOnly
                    value={currency}
                    className="mt-1 h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700 shadow-sm"
                    aria-label="Currency"
                  />
                </div>
              </div>

              {canShowPrices && (
                <div className="mt-3 flex items-center justify-between gap-3 border-t border-slate-100 pt-3">
                  <div className="text-sm text-slate-600">Line total</div>
                  <div className="text-sm font-semibold text-slate-900">
                    {lineTotal}
                  </div>
                </div>
              )}

              {!canShowPrices && (
                <p className="mt-2 text-[11px] text-slate-500">
                  The price is stored on the line item but only shown to users
                  with price permission.
                </p>
              )}

              {/* POC "Advanced fields (optional)" collapsible */}
              <details className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                <summary className="cursor-pointer text-sm font-semibold text-slate-800">
                  Advanced fields (optional)
                </summary>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className={MANUAL_LABEL_CLASS}>
                      Label (library)
                    </label>
                    <input
                      type="text"
                      value={draft.label}
                      onChange={(e) =>
                        onChange({ ...draft, label: e.target.value })
                      }
                      placeholder="Reusable label (optional)"
                      className={MANUAL_FIELD_CLASS}
                    />
                  </div>
                  <div>
                    <label className={MANUAL_LABEL_CLASS}>Item name</label>
                    <input
                      type="text"
                      value={draft.itemName}
                      onChange={(e) =>
                        onChange({ ...draft, itemName: e.target.value })
                      }
                      placeholder="Optional"
                      className={MANUAL_FIELD_CLASS}
                    />
                  </div>
                  <div>
                    <label className={MANUAL_LABEL_CLASS}>Part name</label>
                    <input
                      type="text"
                      value={draft.partName}
                      onChange={(e) =>
                        onChange({ ...draft, partName: e.target.value })
                      }
                      placeholder="Optional"
                      className={MANUAL_FIELD_CLASS}
                    />
                  </div>
                  <div>
                    <label className={MANUAL_LABEL_CLASS}>Machine type</label>
                    <input
                      type="text"
                      value={draft.machineType}
                      onChange={(e) =>
                        onChange({ ...draft, machineType: e.target.value })
                      }
                      placeholder="Optional"
                      className={MANUAL_FIELD_CLASS}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={MANUAL_LABEL_CLASS}>Tooling type</label>
                    <input
                      type="text"
                      value={draft.toolingType}
                      onChange={(e) =>
                        onChange({ ...draft, toolingType: e.target.value })
                      }
                      placeholder="Optional"
                      className={MANUAL_FIELD_CLASS}
                    />
                  </div>
                </div>
              </details>

              {/* POC "Also save/update in reusable library" — quote-capable
                  staff (canGenerateQuote): admin, sales, engineering. */}
              {canSaveToLibrary && (
                <label className="mt-4 flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={draft.saveToLibrary}
                    onChange={(e) =>
                      onChange({ ...draft, saveToLibrary: e.target.checked })
                    }
                    className="h-4 w-4 rounded border-slate-300"
                  />
                  {draft.selectedLibraryId
                    ? "Also update this item in the reusable library"
                    : "Also save in reusable library"}
                </label>
              )}
            </div>
          </div>
        </div>

        {/* Footer — POC: Cancel / Add to quotation */}
        <div className="flex items-center justify-end gap-2 border-t border-slate-200 p-5">
          <PennButton
            type="button"
            variant="secondary"
            size="md"
            onClick={onCancel}
          >
            Cancel
          </PennButton>
          <PennButton
            type="button"
            variant="primary"
            size="md"
            onClick={() => void handleAdd()}
            disabled={!canAdd || saving}
          >
            {saving ? "Adding…" : "Add to quotation"}
          </PennButton>
        </div>
      </div>
    </div>
  );
};

const MdsFilterSelect = ({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string | null;
  options: string[];
  onChange: (value: string | null) => void;
}) => (
  <div>
    <label className="text-[11px] font-semibold uppercase text-slate-500">
      {label}
    </label>
    <select
      className="mt-2 h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-800"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value.trim() || null)}
    >
      <option value="">All</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  </div>
);
